<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { RouterLink, RouterView, useRoute, useRouter } from 'vue-router';
import Toast from 'primevue/toast';
import ConfirmDialog from 'primevue/confirmdialog';
import DynamicDialog from 'primevue/dynamicdialog';
import MainNav from '@/components/MainNav.vue';
import LayoutToggle from '@/components/LayoutToggle.vue';
import AjaxLoader from '@/components/AjaxLoader.vue';
import { useLayoutStore } from '@/stores/layout';
import { useAuthStore } from '@/stores/auth';
import { landingRouteFor } from '@/router/landing';
import { useNotifier } from '@/composables/notifier';
import { useIdleLogout, IDLE_TIMEOUT_MS } from '@/composables/useIdleLogout';
import emslLogo from '@/assets/images/Horizontal-EMSL-logo.png';

const route = useRoute();
const router = useRouter();
const layout = useLayoutStore();
const auth = useAuthStore();
const notify = useNotifier();
const pageSignature = computed(() => (typeof route.name === 'string' ? route.name : ''));

// Sign out after 30 minutes of inactivity. Arms on login / disarms on logout;
// for a session already restored at mount we start it explicitly below.
const idleMinutes = Math.round(IDLE_TIMEOUT_MS / 60000);
const idle = useIdleLogout({
  onTimeout: () => {
    auth.logout();
    notify.info(
      `You were signed out after ${idleMinutes} minutes of inactivity.`,
      'Session expired',
      { life: 8000 },
    );
    void router.replace({ name: 'main' });
  },
});
onMounted(() => {
  if (auth.isLoggedIn) idle.start();
});

const collapseLabel = computed(() =>
  layout.collapsed ? 'Expand navigation' : 'Collapse navigation',
);

// The logo / home affordance sends signed-in users to their landing page
// (Lab Orders for lab users, Orders otherwise) — never back to the public
// sign-in screen. Logged out, it just points at the sign-in route.
const homeTarget = computed(() => (auth.isLoggedIn ? landingRouteFor(auth) : { name: 'main' }));

// The top masthead is sticky, so pages that want their own sticky chrome
// (e.g. Lab Orders' filter bar) need to offset by its height. We measure it and
// publish the value as a CSS variable on the app root; in side layout there's
// no masthead, so the offset is zero.
const mastheadEl = ref<HTMLElement | null>(null);
const mastheadHeight = ref(0);
let observer: ResizeObserver | null = null;

watch(
  mastheadEl,
  (el) => {
    observer?.disconnect();
    observer = null;
    if (!el || typeof ResizeObserver === 'undefined') {
      mastheadHeight.value = 0;
      return;
    }
    observer = new ResizeObserver(() => {
      mastheadHeight.value = el.offsetHeight;
    });
    observer.observe(el);
    mastheadHeight.value = el.offsetHeight;
  },
  { flush: 'post' },
);

const headerOffset = computed(() =>
  layout.mode === 'top' ? `${mastheadHeight.value}px` : '0px',
);

onBeforeUnmount(() => observer?.disconnect());
</script>

<template>
  <div
    :class="[
      'app',
      `app--layout-${layout.mode}`,
      { 'app--collapsed': layout.mode === 'side' && layout.collapsed },
      pageSignature ? `app--${pageSignature}` : null,
    ]"
    :style="{ '--app-header-offset': headerOffset }"
  >
    <Toast />
    <ConfirmDialog />
    <DynamicDialog />
    <AjaxLoader />

    <!-- Top-bar layout: logo + nav across the top. -->
    <header v-if="layout.mode === 'top'" ref="mastheadEl" class="masthead">
      <div class="masthead__inner">
        <RouterLink :to="homeTarget" class="masthead__logo" aria-label="EMSL home">
          <img :src="emslLogo" alt="EMSL" class="masthead__logo-img" />
        </RouterLink>
        <MainNav class="masthead__nav" orientation="horizontal" />
        <!-- Only for signed-in users: on the public sign-in screen there is no
             nav, so the toggle has nothing to sit beside and it isn't an option
             pre-login anyway. When shown, `.masthead__nav`'s auto left-margin
             keeps it hard against the right edge. -->
        <LayoutToggle v-if="auth.isLoggedIn" class="masthead__toggle" />
      </div>
    </header>

    <!-- Side-ribbon layout: logo + nav down the left. Collapses to an icon rail. -->
    <aside v-else :class="['ribbon', { 'ribbon--collapsed': layout.collapsed }]">
      <div class="ribbon__inner">
        <RouterLink :to="homeTarget" class="ribbon__logo" aria-label="EMSL home">
          <img v-if="!layout.collapsed" :src="emslLogo" alt="EMSL" class="ribbon__logo-img" />
          <!-- Collapsed rail: the compact EMSL diamond mark (matches favicon.svg). -->
          <svg
            v-else
            class="ribbon__logo-mark"
            viewBox="0 0 64 64"
            role="img"
            aria-label="EMSL"
          >
            <polygon points="32,2 62,32 32,62 2,32" fill="#E94B4B" />
            <text
              x="32"
              y="37"
              text-anchor="middle"
              font-family="Arial Black, Arial, sans-serif"
              font-weight="900"
              font-size="14"
              fill="#ffffff"
            >
              EMSL
            </text>
          </svg>
        </RouterLink>

        <MainNav class="ribbon__nav" orientation="vertical" :collapsed="layout.collapsed" />

        <!-- Footer sits at the bottom of the ribbon: collapse + layout switch. -->
        <div class="ribbon__footer">
          <button
            type="button"
            class="ribbon__collapse"
            :title="collapseLabel"
            :aria-label="collapseLabel"
            @click="layout.toggleCollapsed()"
          >
            <i
              :class="['pi', layout.collapsed ? 'pi-angle-double-right' : 'pi-angle-double-left']"
              aria-hidden="true"
            />
          </button>
          <LayoutToggle class="ribbon__toggle" />
        </div>
      </div>
    </aside>

    <main class="app__main">
      <RouterView />
    </main>
  </div>
</template>

<style scoped>
.app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}
.app--layout-side {
  flex-direction: row;
}
.masthead {
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  /* Sticks to the top during scroll. z-index sits above the page chrome but
   * well below PrimeVue's overlays (Toast/ConfirmDialog/DynamicDialog all
   * use 1100+), so dialogs still render on top of the header. */
  position: sticky;
  top: 0;
  z-index: 50;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}
.masthead__inner {
  max-width: 80rem;
  margin: 0 auto;
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
}
.masthead__logo {
  display: inline-block;
  line-height: 0;
  flex-shrink: 0;
}
.masthead__logo-img {
  height: 90px;
  width: auto;
  display: block;
}
.masthead__nav {
  margin-left: auto;
}
.masthead__toggle {
  margin-left: 0.25rem;
}

/* ---- Left-ribbon layout ---- */
.ribbon {
  background: #fff;
  border-right: 1px solid #e5e7eb;
  box-shadow: 1px 0 3px rgba(0, 0, 0, 0.04);
  flex-shrink: 0;
  width: 210px;
  /* Ribbon stays put while the page content scrolls beside it. */
  position: sticky;
  top: 0;
  align-self: flex-start;
  height: 100vh;
  overflow-y: auto;
  z-index: 50;
  transition: width 0.15s ease;
}
.ribbon--collapsed {
  width: 64px;
}
.ribbon__inner {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 1rem;
  padding: 1.25rem 1rem;
  /* Fill the ribbon so the footer's margin-top:auto pins it to the bottom. */
  min-height: 100%;
  box-sizing: border-box;
}
.ribbon--collapsed .ribbon__inner {
  padding: 1.25rem 0.5rem;
  align-items: center;
}
.ribbon__logo {
  display: block;
  line-height: 0;
}
.ribbon__logo-img {
  width: 100%;
  max-width: 170px;
  height: auto;
  display: block;
}
.ribbon__logo-mark {
  display: block;
  width: 32px;
  height: 32px;
  margin: 0 auto;
}

/* Footer holds the collapse + layout controls, pinned to the ribbon bottom. */
.ribbon__footer {
  margin-top: auto;
  padding-top: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.ribbon--collapsed .ribbon__footer {
  flex-direction: column;
}
.ribbon__collapse {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  color: #6b7280;
  cursor: pointer;
  flex-shrink: 0;
  transition: color 0.12s, border-color 0.12s, background 0.12s;
}
.ribbon__collapse:hover {
  color: #2563eb;
  border-color: #93c5fd;
  background: #f8fafc;
}

.app__main {
  flex: 1;
  min-width: 0;
  max-width: 80rem;
  width: 100%;
  margin: 0 auto;
  padding: 1.5rem;
}

/* On narrow screens the ribbon can't afford a full column — drop back to a
 * stacked top strip so page content keeps the full width. */
@media (max-width: 640px) {
  .app--layout-side {
    flex-direction: column;
  }
  .ribbon,
  .ribbon--collapsed {
    width: 100%;
    height: auto;
    position: static;
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
  }
}
</style>
